import React from 'react';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Label from 'src/components/Label';
import { format } from 'date-fns';
import moment from 'moment';
import { convertDateToSelectedTimeZone } from 'src/utils/convertDateToSelectedTimeZone';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import { TableSortLabel } from '@mui/material';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';

type SortOption = {
  sortBy: string;
  sortDirection: 'desc' | 'asc';
};

type MdsType = {
  assessmentDate: string;
  assessmentType: string;
};
interface PatientListTableProps {
  count?: number;
  items?: any;
  onPageChange?: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (event: ChangeEvent<HTMLInputElement>) => void;
  page?: number;
  rowsPerPage?: number;
  tableHead: any[];
  onEditEvent?: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSortRequest?: (event: SortOption) => void;
  sortOptions?: SortOption;
  isFromPagin?: boolean;
  onViewPage?: (event: ChangeEvent<HTMLInputElement>) => void;
  displayPagination?: boolean;
  onCreate?: (event: MdsType) => void;
  /** Automation handle for each row (BOPS-262); keyed by the row's own identity. */
  rowTestId?: (row: any) => string | undefined;
}

const ListingTable: FC<PatientListTableProps> = (props) => {
  const {
    count = 0,
    items = [],
    onPageChange = () => {},
    onRowsPerPageChange,
    onRowClick = () => {},
    page = 0,
    rowsPerPage = 0,
    tableHead,
    onEditEvent = () => {},
    handleSortRequest = () => {},
    sortOptions,
    isFromPagin = false,
    onViewPage = () => {},
    displayPagination = true,
    onCreate = () => {},
    rowTestId,
  } = props;

  const gettheResult = () => {
    if (isFromPagin) return items;
    else return items.slice(page * rowsPerPage, rowsPerPage * page + rowsPerPage);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {tableHead?.map((head) => (
              <TableCell key={head.value || head.label}>
                <TableSortLabel
                  active={
                    (head?.sort && head.value === 'assessmentDate') ||
                    (head?.sort && head.value === 'assessmentType')
                  }
                  direction={
                    head.value === sortOptions?.sortBy ? sortOptions?.sortDirection : 'desc'
                  }
                  onClick={() =>
                    handleSortRequest({
                      sortDirection: sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc',
                      sortBy: head.value,
                    })
                  }
                >
                  {head.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {gettheResult().map((patient: any) => {
            const completionDateFormat = patient?.screeningDate?.split('T')?.at(0);
            const completionDateWithoutTimezone = new Date(
              new Date(completionDateFormat).valueOf() +
                new Date(completionDateFormat).getTimezoneOffset() * 60 * 1000
            );

            return (
              <TableRow
                onClick={() => onRowClick(patient)}
                hover
                sx={{ cursor: 'pointer' }}
                key={patient?.uuid}
                data-testid={rowTestId?.(patient)}
              >
                {tableHead?.map((head) => {
                  if (head?.label === 'Status' && head?.action === 'mds')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient[head.value] === 'IN_PROGRESS' ? (
                          <Label
                            backgroundColor="#F790091F"
                            color="#B54708"
                            text="IN-PROGRESS"
                          />
                        ) : (
                          <Label
                            backgroundColor="#15B79E1F"
                            color="#107569"
                            text="FINALIZED"
                          />
                        )}
                      </TableCell>
                    );

                  if (head?.label === 'Status' && head?.action !== 'episodeOfCare')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        <Label
                          backgroundColor="#15B79E1F"
                          color="#107569"
                          text="COMPLETED"
                        />
                      </TableCell>
                    );

                  if (head?.value === 'episodeTitle')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        <Box
                          display={'flex'}
                          gap={1}
                        >
                          {patient[head?.value]}
                          {patient?.isNonTherapyPatient && (
                            <Box sx={nonTherapyPatientPill}>Non-therapy</Box>
                          )}
                        </Box>
                      </TableCell>
                    );

                  if (head?.value === 'payerPlanStartDate' || head?.value === 'payerPlanEndDate')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient[head?.value]
                          ? moment(
                              new Date(convertDateToSelectedTimeZone(patient[head?.value]))
                            ).format('YYYY-MM-DD HH:mm:ss')
                          : '-'}
                      </TableCell>
                    );
                  if (head?.value === 'edit') {
                    if (patient['status'] !== 'FINALIZED' && head?.action === 'mds') {
                      return (
                        <TableCell
                          key={`${patient?.uuid}-${head.value}`}
                          align="right"
                          title={head.action === 'mds' ? 'Edit MDS' : ''}
                          onClick={() => onEditEvent(patient)}
                        >
                          <Edit02Icon />
                        </TableCell>
                      );
                    } else if (patient['status'] === 'FINALIZED' && head?.action === 'mds') {
                      return (
                        <TableCell
                          key={`${patient?.uuid}-${head.value}`}
                          align="right"
                        />
                      );
                    }
                    return (
                      <TableCell
                        key={`${patient?.uuid}-${head.value}`}
                        align="right"
                        onClick={() => onEditEvent(patient)}
                      >
                        <Edit02Icon />
                      </TableCell>
                    );
                  }

                  if (head?.value === 'arrow' && head?.action !== 'mds')
                    return (
                      <TableCell
                        key={`${patient?.uuid}-${head.value}`}
                        align="right"
                        title={head.action === 'mds' ? 'View MDS' : ''}
                      >
                        <ArrowForwardIcon />
                      </TableCell>
                    );

                  if (head?.value === 'arrow' && head?.action === 'mds')
                    return (
                      <TableCell
                        key={`${patient?.uuid}-${head.value}`}
                        align="right"
                        title={head.action === 'mds' ? 'View MDS' : ''}
                        onClick={() => onViewPage(patient)}
                      >
                        <ArrowForwardIcon />
                      </TableCell>
                    );

                  if (head?.value === 'plus' && head?.action === 'mds')
                    return (
                      <TableCell
                        key={`${patient?.uuid}-${head.value}`}
                        align="center"
                        title={head.action === 'mds' ? 'Create MDS' : ''}
                        onClick={() => onCreate(patient)}
                      >
                        <PlusIcon />
                      </TableCell>
                    );

                  if (head?.value === 'startDate' || head?.value === 'endDate') {
                    const updateDateFormat =
                      patient[head?.value] && patient[head?.value]?.split('T')?.at(0);
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient[head?.value]
                          ? format(
                              new Date(
                                new Date(updateDateFormat).valueOf() +
                                  new Date(updateDateFormat).getTimezoneOffset() * 60 * 1000
                              ),
                              'dd MMM, yyyy'
                            )
                          : '-'}
                      </TableCell>
                    );
                  }
                  if (head.value === 'screeningType' || head.value === 'disciplineOfComplete')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient[head?.value] === 'ACUTE_CARE_SWING_BED'
                          ? 'Acute Care/Swing Bed'
                          : patient[head?.value]
                              ?.replace(/_/g, ' ')
                              ?.replace(
                                /\w\S*/g,
                                (txt: string) =>
                                  txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                              )}
                      </TableCell>
                    );

                  if (head.value === 'payerName') {
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient?.payerPlanEntity?.payerPlanName
                          ? patient?.payerPlanEntity?.payerPlanName
                          : '-'}
                      </TableCell>
                    );
                  }
                  if (head.value === 'dischargeDate')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient?.screeningDate
                          ? moment(completionDateWithoutTimezone)?.format('MMM DD, yyyy')
                          : '-'}{' '}
                      </TableCell>
                    );
                  if (head.value === 'evalDiscipline')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient?.discipline}
                      </TableCell>
                    );
                  if (head.value === 'assessmentDate')
                    return (
                      <TableCell key={`${patient?.uuid}-${head.value}`}>
                        {patient[head.value]
                          ? format(new Date(patient[head.value]), 'dd MMM, yyyy')
                          : '-'}
                      </TableCell>
                    );

                  return (
                    <TableCell key={`${patient?.uuid}-${head.value}`}>
                      {patient[head?.value] === undefined || null ? `-` : patient[head?.value]}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {displayPagination && (
        <TablePagination
          component="div"
          count={count}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </Box>
  );
};

export default ListingTable;

export const nonTherapyPatientPill = {
  borderRadius: '12px',
  background: 'rgba(17, 25, 39, 0.08)',
  padding: '0px 6px',
  color: '#6C737F',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '0.16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
